import {
  incidentDetailsResponseSchema,
  incidentsResponseSchema,
  type SchoolIncidentCreateRequest,
} from "@kindtutor/api-contract";
import {
  localizedApiErrorSchema as apiErrorSchema,
  throwApiError,
} from "@kindtutor/auth-client";

import { apiBaseURL, apiFetch } from "../auth-client";

export async function fetchSchoolIncidents(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać zgłoszeń.");
  return incidentsResponseSchema.parse(await response.json());
}

export async function fetchSchoolIncident(
  schoolId: string,
  incidentId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents/${encodeURIComponent(incidentId)}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się otworzyć zgłoszenia.");
  return incidentDetailsResponseSchema.parse(await response.json());
}

export async function createSchoolIncident(
  schoolId: string,
  input: SchoolIncidentCreateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć zgłoszenia.",
    );
  }
  return incidentDetailsResponseSchema.parse(body);
}

export async function addSchoolIncidentComment(
  schoolId: string,
  incidentId: string,
  body: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents/${encodeURIComponent(incidentId)}/comments`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ body, visibility: "school" }),
    },
  );
  const result: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(result);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się dodać odpowiedzi.",
    );
  }
  return incidentDetailsResponseSchema.parse(result);
}

export async function uploadSchoolIncidentAttachment(
  schoolId: string,
  incidentId: string,
  file: File,
) {
  const query = new URLSearchParams({ filename: file.name });
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents/${encodeURIComponent(incidentId)}/attachments?${query}`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": file.type },
      body: file,
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się dodać załącznika.");
  return incidentDetailsResponseSchema.parse(await response.json());
}

export async function downloadSchoolIncidentAttachment(
  schoolId: string,
  incidentId: string,
  attachmentId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents/${encodeURIComponent(incidentId)}/attachments/${encodeURIComponent(attachmentId)}/content`,
    { credentials: "include", headers: { Accept: "*/*" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się pobrać załącznika.");
  const disposition = response.headers.get("Content-Disposition");
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/)?.[1];
  return {
    blob: await response.blob(),
    filename: encoded ? decodeURIComponent(encoded) : "zalacznik",
  };
}

export async function deleteSchoolIncidentAttachment(
  schoolId: string,
  incidentId: string,
  attachmentId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/incidents/${encodeURIComponent(incidentId)}/attachments/${encodeURIComponent(attachmentId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się usunąć załącznika.");
  return incidentDetailsResponseSchema.parse(await response.json());
}
